import type { BuildingEvent, EventWithId } from "types/all";

import { Paper, Stack } from "@mui/material";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { parseJson } from "utils/dataManipulation";

import {
  useAddBuildingEvent,
  useDeleteBuildingEvent,
  useGetAllBuildings,
  useGetBuilding,
  useGetBuildingEvents,
  useGetKPIs,
  useGetProjectInfo,
  useModifyBuildingEvent,
} from "../../api/hooks/allHooks";
import { BUILDING_EVENTS_MAX_YEAR, BUILDING_EVENTS_MIN_YEAR } from "../../configs/BuildingProfile";
import { WizardContext } from "../../context/Wizard";
import { convertToKilo, perM2ValueToNormal } from "../../utils/general";
import { getUserFromLocalStorage } from "../../utils/localStorage";
import { formatNumberBasedOnUser, valueOrAlternative } from "../../utils/namesManipulation";
import { Button } from "../ButtonNew";
import { EnergyClassTimeline } from "../EPCTimeline";
import { Icon } from "../Icon";
import { LoadingOrEmptyWrapper } from "../LoadingAndEmptyHandler";
import { NumericRangePicker } from "../NumericRangePicker";
import Popover from "../Popover";
import { Table } from "../Table";
import Timeline from "../Timeline";
import { Building, TimelineData } from "./types";

type TableFieldValue = { fieldName: string; value: unknown };

type TableFieldValueWithRowId = TableRowId & { fieldName: string; value: unknown };

type TableRow = BuildingEvent & { isReadOnly?: boolean };

type TableRowId = { rowId: number };
type TimelineTableProps = {
  className?: string;
  projectId?: number;
};

export const BuildingEvents = ({ className, projectId }: TimelineTableProps) => {
  const { t } = useTranslation();
  const user = getUserFromLocalStorage();

  const { data, isLoading: allBuildingIsLoading } = useGetAllBuildings(user?.user_id);
  const { data: building } = useGetBuilding(projectId);

  const { epcInfo, epcIsLoading } = useContext(WizardContext);

  const { data: projectInfo, isLoading: projectInfoIsLoading } = useGetProjectInfo(projectId, {});
  let crremScenario: string | undefined;
  if (projectInfo?.[0]?.crrem_scenario_list) {
    crremScenario = parseJson<string[]>(projectInfo?.[0]?.crrem_scenario_list)?.[0];
  }
  const crremKPIs = useGetKPIs(projectId, crremScenario);
  const crremKPIsIsLoading = crremKPIs.isLoading;
  const { data: events, isLoading: eventsIsLoading } = useGetBuildingEvents(projectId);

  const [scrollDown, setScrollDown] = useState(false);

  const eventsTypesAndSubEventTypes: Record<string, { color?: string; isSelectable?: boolean; options?: string[] }> = {
    "Energy and CO2": {
      color: "bg-orange-400",
      isSelectable: true,
      options: [
        "EPC Expires",
        "Stranding year (CRREM)",
        "Energy Audit required",
        "ESG rating expires",
        "Change of Utility Mix",
      ],
    },
    Maintenance: {
      color: "bg-purple-500",
      isSelectable: true,
      options: ["Elevator", "Heating System", "Cooling System", "Fire protection", "PV-System", "FM-contract expires"],
    },
    "Planned building modernization": {
      color: "bg-core-cathode-green-400",
      isSelectable: false,
      options: [],
    },
    "Rental and financial data": {
      color: "bg-core-blue-500",
      isSelectable: true,
      options: ["Rental contract expires", "Tenant moves out", "Refinancing required"],
    },
  };

  const eventTypeOptions = Object.keys(eventsTypesAndSubEventTypes).filter(
    (type: string) => eventsTypesAndSubEventTypes[type].isSelectable
  );
  const getSubEventTypeOptions = (eventType: string | undefined) =>
    eventType ? eventsTypesAndSubEventTypes[eventType]?.options ?? [] : [];

  const headers = [
    {
      dataKey: "eventName",
      label: t("BuildingProfile.buildingEvents.table.columns.eventName"),
      valueTransformation: (row: EventWithId) => row["eventName"],
    },
    {
      cellType: "dropdown",
      dataKey: "eventType",
      label: t("BuildingProfile.buildingEvents.table.columns.eventType"),
      options: () => eventTypeOptions,
      valueTransformation: (row: EventWithId) => row["eventType"],
    },
    {
      cellType: "dropdown",
      dataKey: "subEventType",
      label: t("BuildingProfile.buildingEvents.table.columns.eventSubType"),
      options: (row: EventWithId) => getSubEventTypeOptions(row["eventType"]),
      valueTransformation: (row: EventWithId) => row["subEventType"],
    },
    {
      dataKey: "toDo",
      label: t("BuildingProfile.buildingEvents.table.columns.toDo"),
      valueTransformation: (row: EventWithId) => row["toDo"],
    },
    {
      contentType: "number",
      dataKey: "year",
      label: t("BuildingProfile.buildingEvents.table.columns.year"),
      valueTransformation: (row: EventWithId) => row["year"],
    },
  ];

  const { mutate: addBuildingEvent } = useAddBuildingEvent();

  const defaultEventType = eventTypeOptions[0];

  const handleNewRowBlur = ({ fieldName, value }: TableFieldValue) => {
    if (!value) return;
    addBuildingEvent({
      data: {
        eventType: defaultEventType,
        [fieldName]: value,
        projectId: Number(projectId),
        subEventType: getSubEventTypeOptions(defaultEventType)?.[0],
      },
      onSettled: () => {
        setTimeout(() => {
          setScrollDown(false);
        }, 500);
      },
      onSuccess: () => {
        setScrollDown(true);
      },
      projectId,
    });
  };

  const handleDuplicateRow = ({ rowId }: TableRowId) => {
    const selectedRow = events?.find((event: EventWithId) => event.id === rowId);
    if (!selectedRow?.eventName) return;
    let newName = selectedRow.eventName + " Copied";
    if (events?.map((event: EventWithId) => event.eventName)?.includes(newName)) {
      newName =
        newName +
        " " +
        (Number(events?.filter((event: EventWithId) => (event?.eventName ?? "")?.indexOf(newName) > -1)?.length ?? 0) +
          1);
    }

    const duplicatedRow: Partial<BuildingEvent> = {};
    Object.keys(selectedRow ?? {})?.forEach((dataKey: string) => {
      if (dataKey !== "id") (duplicatedRow as any)[dataKey] = (selectedRow as any)[dataKey];
    });

    addBuildingEvent({
      data: { ...duplicatedRow, eventName: newName, projectId: Number(projectId) },
      onSettled: () => {
        setTimeout(() => {
          setScrollDown(false);
        }, 500);
      },
      onSuccess: () => {
        setScrollDown(true);
      },
      projectId,
    });
  };

  const { mutate: deleteBuildingEvent } = useDeleteBuildingEvent();
  const handleDelete = ({ rowId }: TableRowId) => {
    deleteBuildingEvent({ projectId, rowId });
  };

  const { mutate: modifyBuildingEvent } = useModifyBuildingEvent();
  const handleBlur = ({ fieldName, rowId, value }: TableFieldValueWithRowId) => {
    modifyBuildingEvent({
      data: { [fieldName]: fieldName === "year" ? Number(value) : value },
      projectId,
      rowId,
    });
  };

  const allEvents: (BuildingEvent & { isReadOnly?: boolean })[] = [...(events ?? [])];

  if (crremKPIs.total_energy_consumption !== undefined)
    allEvents.push({
      eventName: "Building will be stranded",
      eventType: "Energy and CO2",
      isReadOnly: true,
      subEventType: "Stranding year (CRREM)",
      toDo: "Modernize the building",
      year: crremKPIs.stranding_year,
    });

  let epcExpireYear = undefined;

  if (Object.keys(epcInfo)?.length) {
    try {
      epcExpireYear = epcInfo?.valid_until ? new Date(epcInfo?.valid_until).getFullYear() : undefined;
    } catch {
      // Do Nothing
    }
  }

  if (epcExpireYear)
    allEvents.push({
      eventName: "EPC will be expired",
      eventType: "Energy and CO2",
      isReadOnly: true,
      subEventType: "EPC Expires",
      toDo: "Revalidate EPC",
      year: epcExpireYear,
    });

  const retrofits = (data as Building[] | undefined)?.find((building) => building.id === Number(projectId))?.retrofits;
  const retrofitsFlat = retrofits?.map((retrofit) => ({ ...retrofit, ...retrofit?.retrofit_details }));

  retrofitsFlat?.forEach((retrofit) => {
    const totalInvestmentCost = retrofit.investments;
    if (totalInvestmentCost !== undefined || totalInvestmentCost !== null)
      allEvents.push({
        eventName: retrofit.name,
        eventType: "Planned building modernization",
        isReadOnly: true,
        subEventType: `${retrofit.sub_component} || ${valueOrAlternative(
          formatNumberBasedOnUser(convertToKilo(perM2ValueToNormal({ building, value: totalInvestmentCost })))
        )} k€`,
        toDo: retrofit.measure_description,
        year: retrofit.year,
      });
  });

  const timelineData: TimelineData = [];

  (allEvents ?? [])?.forEach((event) => {
    if (typeof event.year === "number")
      timelineData.push({
        bgColor: eventsTypesAndSubEventTypes?.[event.eventType || ""]?.color ?? "bg-lds-surface-bright",
        label: event.eventName ?? "",
        tooltip: (
          <div>
            <p>{event.eventName}</p>
            <p>{event.eventType}</p>
            <p>{event.subEventType}</p>
            <p>{event.toDo}</p>
          </div>
        ),
        year: event.year,
      });
  });

  const minYear = BUILDING_EVENTS_MIN_YEAR;
  const maxYear = BUILDING_EVENTS_MAX_YEAR;
  const [yearRange, setYearRange] = useState([minYear, maxYear]);

  const dataIsLoading =
    eventsIsLoading || allBuildingIsLoading || epcIsLoading || crremKPIsIsLoading || projectInfoIsLoading;
  return (
    <LoadingOrEmptyWrapper height="20rem" showLoading={dataIsLoading}>
      <Paper className={className}>
        <Stack className={`  gap-4 py-3 `}>
          <span className="font-ds-headline-lg  ">Building Events</span>
          <Popover
            anchorOrigin={{ horizontal: "right", vertical: "top" }}
            contentEl={
              <NumericRangePicker
                changeByTyping
                max={maxYear}
                min={minYear}
                setValue={setYearRange}
                step={1}
                value={yearRange}
              />
            }
            triggerEl={
              <Button variant="tertiary">
                <Stack className="font-ds-headline-md cursor-pointer items-center justify-center gap-2 ">
                  <span>
                    {yearRange[0]}-{yearRange[1]}
                  </span>
                  <Icon iconName="Settings" />
                </Stack>
              </Button>
            }
          />
        </Stack>
        <EnergyClassTimeline maxYear={yearRange[1]} minYear={yearRange[0]} />
        <Timeline data={timelineData} maxYear={yearRange[1]} minYear={yearRange[0]} />
        <Table
          addable
          className="mt-12 max-h-100"
          columnsMinWidth="180px"
          data={allEvents}
          editable={(row: TableRow) => !row.isReadOnly}
          headers={headers}
          idColumnName="id"
          newRowDataKey="eventName"
          onAddNewRow={({ fieldName, value }: TableFieldValue) => handleNewRowBlur({ fieldName, value })}
          onBlur={({ fieldName, rowId, value }: TableFieldValueWithRowId) => handleBlur({ fieldName, rowId, value })}
          onCopy={({ rowId }: TableRowId) => {
            handleDuplicateRow({ rowId });
          }}
          onDelete={({ rowId }: TableRowId) => handleDelete({ rowId })}
          rowRemoveable={(row: TableRow) => !row.isReadOnly}
          scrollDown={scrollDown}
        />
      </Paper>
    </LoadingOrEmptyWrapper>
  );
};
